import { AfterViewInit, ChangeDetectorRef, Component, effect, EffectRef, ElementRef, inject, Injector, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { Splide } from '@splidejs/splide';
import { BenefitsFullscreenCarouselComponent } from '../../benefits-fullscreen-carousel/benefits-fullscreen-carousel.component';
import { ComponentPortal } from '@angular/cdk/portal';
import { PortalService } from '@services/portal.service';

@Component({
    selector: 'app-benefits-carousel',
    imports: [],
    templateUrl: './benefits-carousel.component.html',
    styleUrl: './benefits-carousel.component.css'
})
export class BenefitsCarouselComponent implements AfterViewInit, OnChanges, OnDestroy{

  // VIEW CHILD
  @ViewChild('carousel', {static: true, read: ElementRef})
  public carousel!: ElementRef;

  // INPUTS
  @Input({required: true})
  public title!: string;

  @Input({required: true})
  public data!: {img: string[], text: string[]};


  // SERVICES
  private readonly portalService:   PortalService     = inject(PortalService);
  private readonly injector:        Injector          = inject(Injector);
  private readonly cdr:             ChangeDetectorRef = inject(ChangeDetectorRef);
  private splide!:                  Splide;
  private isInitialized = false; // Guard flag
  private initFrame: number | null = null;

  // EFFECTS
  private readonly onPortalChange:  EffectRef         = effect((): void => {
    
    if(this.portalService.portalContent() === null) this.cdr.markForCheck();
    
  });

  //LIFECYCLE HOOKS
  ngAfterViewInit(): void {

    // Solo inicializar si no se ha hecho antes
    if (!this.isInitialized) {
      this.initComponent();
    }

  }

  ngOnChanges(): void {

    /**
       * Flujo de reinicialización:
       * 1. Se destruye la instancia actual de Splide porque los inputs han cambiado.
       * 2. Se cancela cualquier reinicialización pendiente para evitar montar Splide dos veces.
       * 3. Se espera al siguiente frame del navegador para que Angular termine de pintar
       *    los nuevos datos en el DOM del carrusel.
       * 4. Se crea una nueva instancia de Splide sobre el DOM ya actualizado.
     */
    if (this.isInitialized && this.splide) {

      this.splide.destroy();

      this.isInitialized = false;

      if(this.initFrame !== null) cancelAnimationFrame(this.initFrame);
      
      this.initFrame = requestAnimationFrame((): void => {
        this.initFrame = null;
        this.initComponent();
      });

    }
  }

  ngOnDestroy(): void {

    if (this.splide) {

      this.splide.destroy();

      this.isInitialized = false;

    }

    if(this.initFrame !== null) cancelAnimationFrame(this.initFrame);

    this.onPortalChange.destroy();
    
  }

  public get total(): number {
    return this.data.img.length;
  }

  // METHODS
  public initComponent(): void {
    
    if (this.isInitialized) return; // Prevenir múltiples inicializaciones
    
    this.splide = new Splide(this.carousel.nativeElement, {
      type: 'loop',
      perPage: 1,
      perMove: 1,
      gap: '1rem',
      arrows:     false,
      drag:       this.total === 1 ? false : true,
      pagination: this.total === 1 ? false : true,
    });
    
    this.splide.mount();

    this.splide.on('click', (): void => this.openFullscreenCarousel());
    
    this.isInitialized = true;
  }

  public openFullscreenCarousel(): void {
    
    if (!this.splide) return;

    const values = {
      index:        this.splide.index,
      title:        this.title,
      images:       this.data.img,
      descriptions: this.data.text,
    }

    const customInjector: Injector = Injector.create({
      providers: [{
        provide: 'CarouselData', 
        useValue: values
      }],
      parent: this.injector,
    })

    const component: ComponentPortal<BenefitsFullscreenCarouselComponent> = new ComponentPortal<BenefitsFullscreenCarouselComponent>(BenefitsFullscreenCarouselComponent, null, customInjector);

    this.portalService.setPortal(component)

  }

}
